export type AggregatesLayout = 'horizontal' | 'vertical' | 'columns'

export const AGGREGATE_OPS = ['sum', 'avg', 'min', 'max', 'count'] as const
export type AggregateOp = (typeof AGGREGATE_OPS)[number]

export function sanitizeAggregatesLayout(v: unknown): AggregatesLayout {
  return v === 'vertical' || v === 'columns' ? v : 'horizontal'
}

export function parseAggregatePair(pair: string): { field: string; op: string } {
  const idx = pair.indexOf(':')
  return idx === -1 ? { field: pair, op: '' } : { field: pair.slice(0, idx), op: pair.slice(idx + 1) }
}

export function aggregatesForField(aggregates: string[], field: string): string[] {
  return aggregates.filter((pair) => parseAggregatePair(pair).field === field)
}

export function formatAggregateValue(
  raw: number | string | null | undefined,
  digits?: { precision?: number; scale?: number },
  op?: string,
): string {
  if (raw == null) return '—'
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (Number.isNaN(n)) return String(raw)
  if (op === 'count') return String(Math.round(n))
  const precision = Number(digits?.precision)
  const scale = Number(digits?.scale)
  const p = Number.isFinite(precision) && precision > 0 ? Math.floor(precision) : 15
  const s = Number.isFinite(scale) && scale >= 0 ? Math.floor(scale) : 2
  const factor = 10 ** s
  return (Math.round(n * factor) / factor).toFixed(s)
}

/** Stored order wins for known fields; new columns append in column (LTR) order. */
export function mergeAggregatesFieldOrder(columnOrder: string[], stored: string[] | undefined): string[] {
  const known = new Set(columnOrder)
  const fromStore = (stored ?? []).filter((name) => known.has(name))
  const seen = new Set(fromStore)
  return [...fromStore, ...columnOrder.filter((name) => !seen.has(name))]
}

export function moveAggregatesField(order: string[], from: string, to: string): string[] {
  const i = order.indexOf(from)
  const j = order.indexOf(to)
  if (i < 0 || j < 0 || i === j) return order
  const next = order.slice()
  next.splice(i, 1)
  next.splice(j, 0, from)
  return next
}

/** Selected pairs in field order, then op order (sum → count). Unknown pairs keep tail. */
export function orderAggregatePairs(aggregates: string[], fieldOrder: string[]): string[] {
  const set = new Set(aggregates)
  const out: string[] = []
  for (const field of fieldOrder) {
    for (const op of AGGREGATE_OPS) {
      const pair = `${field}:${op}`
      if (set.has(pair)) out.push(pair)
    }
  }
  for (const pair of aggregates) {
    if (!out.includes(pair)) out.push(pair)
  }
  return out
}

export function toggleAggregatePair(aggregates: string[], pair: string, fieldOrder: string[]): string[] {
  const next = aggregates.includes(pair)
    ? aggregates.filter((a) => a !== pair)
    : [...aggregates, pair]
  return orderAggregatePairs(next, fieldOrder)
}
